import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import Pagination from '../components/shared/Pagination';
import FilterBar from '../components/shared/FilterBar';
import { useResizableColumns, ResizeHandle, useSortableData, SortableHeader } from '../components/shared/tableUtils';
import {
  fetchAllMediatorAnalytics,
  aggregateMediatorsStats,
} from '../services/DashboardAnalyticsService';

/**
 * MoM change pill. Reads { direction, percent } from the API; falls back
 * to a neutral '—' when the data isn't available. Down = red, otherwise
 * green. Inert when percent is null (no real comparison possible).
 */
function ChangePill({ change, suffix }) {
  const dir = change?.direction || 'flat';
  const pct = change?.percent;
  const isNew = change?.isNew;
  const isDown = dir === 'down';
  const bg = isDown ? '#fff1f1' : '#e8fdf5';
  const color = isDown ? '#a60003' : '#00a666';
  let label;
  if (isNew) {
    label = `+${change.value} new`;
  } else if (pct == null) {
    label = '—';
  } else {
    const sign = pct > 0 ? '+' : '';
    label = `${sign}${pct}%`;
  }
  const trail = suffix || `vs ${change?.compareLabel || 'last month'}`;
  return (
    <div className="inline-flex items-center gap-[5px] rounded-full whitespace-nowrap" style={{ padding: '4px 8px 5px 8px', backgroundColor: bg }}>
      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className="shrink-0">
        {isDown ? (
          <>
            <path d="M1.5 3.5L4.5 6.5L6.5 4.5L10.5 8.5" stroke={color} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M7.5 8.5H10.5V5.5" stroke={color} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
          </>
        ) : (
          <>
            <path d="M1.5 8.5L4.5 5.5L6.5 7.5L10.5 3.5" stroke={color} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M7.5 3.5H10.5V6.5" stroke={color} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
          </>
        )}
      </svg>
      <span className="text-[10px] font-medium whitespace-nowrap" style={{ fontFamily: 'Inter Tight, sans-serif', color }}>{label}</span>
      <span className="text-[#0e1e20] text-[10px] font-medium whitespace-nowrap" style={{ fontFamily: "'Inter Tight', sans-serif" }}>{trail}</span>
    </div>
  );
}

export default function Mediators() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const initials = ((user?.firstName || 'U')[0] + ((user?.lastName || '')[0] || '')).toUpperCase();
  const [mediators, setMediators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalMediators, setTotalMediators] = useState(0);
  const [stats, setStats] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [pageSize, setPageSize] = useState(10);
  const STATUS_OPTIONS = ['', 'Active', 'Inactive'];

  // Period chip text — reflects the active filter on this page so the
  // tiles show what slice they're describing. Mediators has no date
  // filter; the chip echoes the status filter (or "All" by default).
  const periodLabel = statusFilter || 'All';

  const { widths: colWidths, setWidth: setColWidth } = useResizableColumns(
    'mediators.list',
    { name: 240, email: 220, cases: 120, inquiries: 120, scaleMed: 180 }
  );
  const { sortedRows: sortedMediators, sort: tableSort, requestSort: requestTableSort } = useSortableData(
    mediators,
    {
      name: (r) => r.name,
      email: (r) => r.email,
      cases: (r) => r.cases,
      inquiries: (r) => r.inquiries,
      scaleMed: (r) => r.scaleMed,
      lastCase: (r) => r.lastCaseRaw,
    }
  );

  useEffect(() => { setCurrentPage(1); }, [searchQuery, statusFilter]);

  // Sequence guard so stale responses (slow network, in-flight when the
  // user changes a filter) cannot overwrite fresh ones. Each effect run
  // bumps the counter; the response handlers compare against it.
  const reqSeqRef = useRef(0);

  // Stats now narrow with the same filters the list uses (search +
  // status) so the KPI tiles agree with the visible rows.
  useEffect(() => {
    const mySeq = ++reqSeqRef.current;
    const handler = setTimeout(() => {
    // Only show the spinner once the debounced request actually fires —
    // setting loading on every keystroke caused a visible flicker.
    setLoading(true);
    const params = new URLSearchParams({ page: currentPage, limit: pageSize });
    if (searchQuery) params.set('search', searchQuery);
    if (statusFilter) params.set('status', statusFilter.toLowerCase());
    // Re-fetch KPI stats with the same filter so the tiles match the
    // visible list.
    const statsParams = new URLSearchParams();
    if (searchQuery) statsParams.set('search', searchQuery);
    if (statusFilter) statsParams.set('status', statusFilter.toLowerCase());
    const statsQs = statsParams.toString();
    // CM-BE owns newThisMonth + lostThisMonth (scale-be has no
    // mediator-onboarding history endpoint). Active + avgCasesPerActive
    // are overlaid from scale-be's per-mediator analytics below.
    const cmStatsPromise = api.get(`/mediators/stats${statsQs ? `?${statsQs}` : ''}`)
      .then(res => res.data?.data || null)
      .catch((err) => { console.error(err); return null; });
    // Single /mediators?... call powers BOTH the list rendering AND the
    // scale-be analytics fan-out (we extract userIds from the same
    // response). Saves a duplicate roundtrip per filter change.
    const listPromise = api.get(`/mediators?${params.toString()}`)
      .then(res => res.data || { data: [], total: 0, pages: 1 })
      .catch((err) => { console.error(err); return { data: [], total: 0, pages: 1 }; });
    listPromise.then((listRes) => {
      if (mySeq !== reqSeqRef.current) return;
      const mapped = (listRes.data || []).map(m => {
        const fullName = m.name || `${m.firstName || ''} ${m.lastName || ''}`.trim() || 'Unknown';
        return {
          id: m.id,
          name: fullName,
          initials: fullName ? fullName.split(' ').filter(Boolean).map(n => n[0]).join('').toUpperCase().slice(0, 2) : 'NA',
          email: m.email || '',
          cases: m.totalCases || m.cases_count || 0,
          inquiries: m.inquiries_count || 0,
          // This column is wired to the mediator's account status — it
          // is NOT a Stripe Connect indicator. Naming it "Status" with
          // Active/Inactive matches the actual data source.
          scaleMed: (m.status || '').toLowerCase() === 'active' ? 'Active' : 'Inactive',
          lastCase: m.lastCaseDate ? new Date(m.lastCaseDate + 'T00:00:00').toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '-',
          lastCaseRaw: m.lastCaseDate || '',
        };
      });
      setMediators(mapped);
      setTotalMediators(listRes.total || listRes.data?.length || 0);
      setTotalPages(listRes.pages || 1);
    });
    // Fan out per-mediator analytics against scale-be in parallel with
    // the cm-be stats call. Narrows in lockstep with the list filter
    // (search + status) since we derive userIds from the same listPromise.
    const scalePromise = listPromise.then((listRes) => {
      const userIds = (listRes.data || []).map((m) => m.userId).filter(Boolean);
      return fetchAllMediatorAnalytics(userIds).catch(() => []);
    });
    Promise.all([cmStatsPromise, scalePromise]).then(([cmStats, scaleRows]) => {
      if (mySeq !== reqSeqRef.current) return;
      const scaleStats = Array.isArray(scaleRows) && scaleRows.length > 0
        ? aggregateMediatorsStats(scaleRows)
        : null;
      // newThisMonth / lostThisMonth / their change pills stay on cm-be.
      // active + avgCasesPerActive override with scale-derived numbers.
      const merged = {
        ...(cmStats || {}),
        ...(scaleStats
          ? {
              active: scaleStats.active,
              avgCasesPerActive: scaleStats.avgCasesPerActive,
            }
          : {}),
      };
      setStats(merged);
    }).finally(() => {
      if (mySeq !== reqSeqRef.current) return;
      setLoading(false);
    });
    }, 250);
    return () => clearTimeout(handler);
  }, [currentPage, pageSize, searchQuery, statusFilter]);

  return (
    <div className="flex flex-col min-h-screen">
      {/* Header Bar - 60px */}
      <div
        className="flex items-center justify-between border-b border-[#e5e5e5]"
        style={{ height: 60, padding: '14px 24px' }}
      >
        <h1
          className="text-[#0e1e20] text-sm font-semibold leading-[1.5]"
          style={{ fontFamily: "'Inter Tight', sans-serif" }}
        >
          Mediators
        </h1>
        <div className="flex items-center gap-4">
          {/* Avatar */}
          <div className="w-8 h-8 rounded-full bg-[#115e59] flex items-center justify-center">
            <span className="text-white text-xs font-semibold" style={{ fontFamily: 'Inter, sans-serif' }}>{initials}</span>
          </div>
        </div>
      </div>

      {/* Filters (above tiles, drives both /mediators/stats KPIs and /mediators list) */}
      <div style={{ padding: '20px 24px 0 24px' }}>
        <FilterBar
          searchValue={searchQuery}
          onSearchChange={setSearchQuery}
          searchPlaceholder="Search by name, email, or specialization..."
          statusValue={statusFilter}
          onStatusChange={setStatusFilter}
          statusOptions={STATUS_OPTIONS.filter(Boolean).map((s) => ({ label: s, value: s }))}
        />
      </div>

      {/* KPI Cards Section */}
      <div
        className="flex gap-3 border-b border-[#e5e5e5]"
        style={{ padding: '20px 24px 26px 24px', width: '100%' }}
      >
        {/* Card 1 - Active Mediators */}
        <div className="flex-1 min-w-0 rounded-[10px] border border-[#e5e5e5] bg-white flex flex-col">
          <div className="flex items-center gap-2 px-3.5 py-3 border-b border-[#e5e5e5]">
            <span className="text-[#0e1e20] text-xs font-medium leading-[1.5]" style={{ fontFamily: "'Inter Tight', sans-serif" }}>
              Active Mediators
            </span>
            <span
              className="text-[10px] font-semibold text-gray-600 bg-gray-100 rounded-md whitespace-nowrap"
              style={{ padding: '3px 7px', fontFamily: 'Inter Tight' }}
            >
              {periodLabel}
            </span>
          </div>
          <div className="flex flex-col items-end justify-end flex-1 px-3.5 pb-3.5 pt-5 gap-3.5">
            <span className="text-[#0e1e20] text-[30px] font-medium leading-none self-start" style={{ fontFamily: "'Inter Tight', sans-serif" }}>
              {stats == null ? '\u2014' : stats.active}
            </span>
            <ChangePill change={stats?.change?.active} />
          </div>
        </div>

        {/* Card 2 - New This Month */}
        <div className="flex-1 min-w-0 rounded-[10px] border border-[#e5e5e5] bg-white flex flex-col">
          <div className="flex items-center gap-2 px-3.5 py-3 border-b border-[#e5e5e5]">
            <span className="text-[#0e1e20] text-xs font-medium leading-[1.5]" style={{ fontFamily: "'Inter Tight', sans-serif" }}>
              New This Month
            </span>
            <span
              className="text-[10px] font-semibold text-gray-600 bg-gray-100 rounded-md whitespace-nowrap"
              style={{ padding: '3px 7px', fontFamily: 'Inter Tight' }}
            >
              {periodLabel}
            </span>
          </div>
          <div className="flex flex-col items-end justify-end flex-1 px-3.5 pb-3.5 pt-5 gap-3.5">
            <span className="text-[#0e1e20] text-[30px] font-medium leading-none self-start" style={{ fontFamily: "'Inter Tight', sans-serif" }}>
              {stats == null ? '\u2014' : stats.newThisMonth}
            </span>
            <ChangePill change={stats?.change?.newThisMonth} />
          </div>
        </div>

        {/* Card 3 - Lost this Month */}
        <div className="flex-1 min-w-0 rounded-[10px] border border-[#e5e5e5] bg-white flex flex-col">
          <div className="flex items-center gap-2 px-3.5 py-3 border-b border-[#e5e5e5]">
            <span className="text-[#0e1e20] text-xs font-medium leading-[1.5]" style={{ fontFamily: "'Inter Tight', sans-serif" }}>
              Lost this Month
            </span>
            <span
              className="text-[10px] font-semibold text-gray-600 bg-gray-100 rounded-md whitespace-nowrap"
              style={{ padding: '3px 7px', fontFamily: 'Inter Tight' }}
            >
              {periodLabel}
            </span>
          </div>
          <div className="flex flex-col items-end justify-end flex-1 px-3.5 pb-3.5 pt-5 gap-3.5">
            <span className="text-[#0e1e20] text-[30px] font-medium leading-none self-start" style={{ fontFamily: "'Inter Tight', sans-serif" }}>
              {stats == null ? '\u2014' : stats.lostThisMonth}
            </span>
            <ChangePill change={stats?.change?.lostThisMonth} suffix="vs previous month" />
          </div>
        </div>

        {/* Card 4 - Avg Cases per Mediator */}
        <div className="flex-1 min-w-0 rounded-[10px] border border-[#e5e5e5] bg-white flex flex-col">
          <div className="flex items-center gap-2 px-3.5 py-3 border-b border-[#e5e5e5]">
            <span className="text-[#0e1e20] text-xs font-medium leading-[1.5]" style={{ fontFamily: "'Inter Tight', sans-serif" }}>
              Avg Cases per Mediator
            </span>
            <span
              className="text-[10px] font-semibold text-gray-600 bg-gray-100 rounded-md whitespace-nowrap"
              style={{ padding: '3px 7px', fontFamily: 'Inter Tight' }}
            >
              {periodLabel}
            </span>
          </div>
          <div className="flex flex-col items-end justify-end flex-1 px-3.5 pb-3.5 pt-5 gap-3.5">
            <span className="text-[#0e1e20] text-[30px] font-medium leading-none self-start" style={{ fontFamily: "'Inter Tight', sans-serif" }}>
              {stats == null ? '\u2014' : stats.avgCasesPerActive}
            </span>
            <ChangePill change={stats?.change?.avgCasesPerActive} suffix="vs last period" />
          </div>
        </div>
      </div>

      {/* Table Section */}
      <div className="flex flex-col gap-6 relative" style={{ padding: '24px 24px 32px 24px' }}>
        {loading && (
          <div className="absolute inset-0 bg-white/80 z-10 flex items-center justify-center">
            <div className="flex flex-col items-center gap-3">
              <div className="w-8 h-8 border-2 border-[#115e59] border-t-transparent rounded-full animate-spin" />
              <span className="text-sm text-[#5b5a64] font-['Inter']">Loading...</span>
            </div>
          </div>
        )}

        {/* Data Table */}
        <div className="rounded-lg border border-[#e5e5e5] overflow-hidden">
          {/* Table Header Row */}
          <div className="flex items-center bg-[#f9fafb] h-[45px] px-10 border-b border-[#e5e5e5]">
            <div className="flex items-center h-full px-3.5 relative" style={{ width: colWidths.name }}>
              <SortableHeader sortKey="name" sort={tableSort} onSort={requestTableSort} className="text-[#5b5a64] text-xs font-medium" style={{ fontFamily: 'Inter, sans-serif' }}>Mediator</SortableHeader>
              <ResizeHandle onResize={setColWidth('name')} getCurrentWidth={() => colWidths.name} />
            </div>
            <div className="flex items-center h-full px-3.5 relative" style={{ width: colWidths.email }}>
              <SortableHeader sortKey="email" sort={tableSort} onSort={requestTableSort} className="text-[#5b5a64] text-xs font-medium" style={{ fontFamily: 'Inter, sans-serif' }}>Email</SortableHeader>
              <ResizeHandle onResize={setColWidth('email')} getCurrentWidth={() => colWidths.email} />
            </div>
            <div className="flex items-center h-full px-3.5 relative" style={{ width: colWidths.cases }}>
              <SortableHeader sortKey="cases" sort={tableSort} onSort={requestTableSort} className="text-[#5b5a64] text-xs font-medium" style={{ fontFamily: 'Inter, sans-serif' }}>Cases</SortableHeader>
              <ResizeHandle onResize={setColWidth('cases')} getCurrentWidth={() => colWidths.cases} />
            </div>
            <div className="flex items-center h-full px-3.5 relative" style={{ width: colWidths.inquiries }}>
              <SortableHeader sortKey="inquiries" sort={tableSort} onSort={requestTableSort} className="text-[#5b5a64] text-xs font-medium" style={{ fontFamily: 'Inter, sans-serif' }}>Inquiries</SortableHeader>
              <ResizeHandle onResize={setColWidth('inquiries')} getCurrentWidth={() => colWidths.inquiries} />
            </div>
            <div className="flex items-center h-full px-3.5 relative" style={{ width: colWidths.scaleMed }}>
              <SortableHeader sortKey="scaleMed" sort={tableSort} onSort={requestTableSort} className="text-[#5b5a64] text-xs font-medium" style={{ fontFamily: 'Inter, sans-serif' }}>Status</SortableHeader>
              <ResizeHandle onResize={setColWidth('scaleMed')} getCurrentWidth={() => colWidths.scaleMed} />
            </div>
            <div className="flex-1 flex items-center h-full px-3.5 min-w-0">
              <SortableHeader sortKey="lastCase" sort={tableSort} onSort={requestTableSort} className="text-[#5b5a64] text-xs font-medium" style={{ fontFamily: 'Inter, sans-serif' }}>Last Case</SortableHeader>
            </div>
          </div>

          {/* Table Rows */}
          {!loading && mediators.length === 0 && (
            <div className="flex items-center justify-center" style={{ height: 120 }}>
              <span className="text-sm text-[#5b5a64] font-['Inter']">No mediators found</span>
            </div>
          )}
          {sortedMediators.map((mediator) => (
            <div
              key={mediator.id}
              className="flex items-center h-[63px] px-10 border-b border-[#e5e5e5] cursor-pointer hover:bg-[#f9fafb] transition-colors"
              onClick={() => navigate(`/mediators/${mediator.id}`)}
            >
              <div className="flex items-center gap-2 h-full px-3.5 min-w-0" style={{ width: colWidths.name }}>
                <div className="w-7 h-7 rounded-full bg-[#115e59] flex items-center justify-center shrink-0">
                  <span className="text-white text-[10px] font-semibold" style={{ fontFamily: 'Inter, sans-serif' }}>{mediator.initials}</span>
                </div>
                <span className="text-[#0e1e20] text-[13px] font-medium truncate" style={{ fontFamily: 'Inter, sans-serif' }}>{mediator.name}</span>
              </div>
              <div className="flex items-center h-full px-3.5 min-w-0" style={{ width: colWidths.email }}>
                <span className="text-[#5b5a64] text-xs truncate" style={{ fontFamily: 'Inter, sans-serif' }}>{mediator.email}</span>
              </div>
              <div className="flex items-center h-full px-3.5" style={{ width: colWidths.cases }}>
                <span className="text-[#0e1e20] text-[13px]" style={{ fontFamily: 'Inter, sans-serif' }}>{mediator.cases}</span>
              </div>
              <div className="flex items-center h-full px-3.5" style={{ width: colWidths.inquiries }}>
                <span className="text-[#0e1e20] text-[13px]" style={{ fontFamily: 'Inter, sans-serif' }}>{mediator.inquiries}</span>
              </div>
              <div className="flex items-center h-full px-3.5" style={{ width: colWidths.scaleMed }}>
                {mediator.scaleMed === 'Active' ? (
                  <span className="px-2 py-[3px] rounded-full bg-[#e8fdf5] text-[#115e59] text-[11px] font-medium" style={{ fontFamily: 'Inter, sans-serif' }}>
                    Active
                  </span>
                ) : (
                  <span className="px-2 py-[3px] rounded-full bg-[#fde8e9] text-[#dc2626] text-[11px] font-medium" style={{ fontFamily: 'Inter, sans-serif' }}>
                    Inactive
                  </span>
                )}
              </div>
              <div className="flex-1 flex items-center h-full px-3.5 min-w-0">
                <span className="text-[#5b5a64] text-xs truncate" style={{ fontFamily: 'Inter, sans-serif' }}>{mediator.lastCase}</span>
              </div>
            </div>
          ))}

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalEntries={totalMediators}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={setPageSize}
          />
        </div>
      </div>
    </div>
  );
}
